import { create } from 'zustand';

import type { ChatMessage, ContentBlock } from '~/features/chat/types/chat.types';

/** Per-context conversation state. One per logical area (e.g. catalog-ops:batch:X29d4a). */
export interface AgentPanelContextState {
  conversationId: string | null;
  messages: ChatMessage[];
  isStreaming: boolean;
  streamingMessageId: string | null;
  error: string | null;
}

interface AgentPanelState {
  byContext: Record<string, AgentPanelContextState>;
}

interface AgentPanelActions {
  /** Returns the state for a context, creating an empty one if missing. */
  getOrInitContext: (key: string) => AgentPanelContextState;
  /** Seeds a brand-new conversation: stores conversationId + the briefing message. */
  seedBriefing: (key: string, payload: { conversationId: string; message: ChatMessage }) => void;
  /** Appends a message to a context. */
  addMessage: (key: string, message: ChatMessage) => void;
  /** Merges fields into an existing message in the context. */
  updateMessage: (key: string, messageId: string, updates: Partial<ChatMessage>) => void;
  /** Concatenates a token delta into a streaming text/markdown block. */
  appendDelta: (key: string, messageId: string, blockIndex: number, delta: string) => void;
  /** Toggles the streaming flag and tracks the active streaming message id. */
  setStreaming: (key: string, isStreaming: boolean, messageId?: string | null) => void;
  /** Records a panel-level error (e.g. conversation-create failure). */
  setError: (key: string, message: string | null) => void;
  /** Wipes a single context. */
  clearContext: (key: string) => void;
}

const EMPTY: AgentPanelContextState = {
  conversationId: null,
  messages: [],
  isStreaming: false,
  streamingMessageId: null,
  error: null,
};

/**
 * Per-context Lily Agent panel store. Keyed by `contextKey` so each page or
 * batch gets its own conversation state, isolated from the global `useChatStore`.
 *
 * The set of well-known keys + their factory builders is defined in
 * `~/features/chat/types/agent-panel-context-key`. Prefer the factory
 * (`agentPanelContextKey.experimentsDetail(id)`) over raw strings at call
 * sites so prefix changes land in one place.
 */
export const useAgentPanelStore = create<AgentPanelState & AgentPanelActions>()((set, get) => ({
  byContext: {},

  getOrInitContext: (key) => {
    const existing = get().byContext[key];
    if (existing) return existing;
    set((state) => ({ byContext: { ...state.byContext, [key]: { ...EMPTY } } }));
    return get().byContext[key] ?? { ...EMPTY };
  },

  seedBriefing: (key, payload) => {
    set((state) => ({
      byContext: {
        ...state.byContext,
        [key]: {
          ...(state.byContext[key] ?? EMPTY),
          conversationId: payload.conversationId,
          messages: [payload.message],
          error: null,
        },
      },
    }));
  },

  addMessage: (key, message) => {
    set((state) => {
      const ctx = state.byContext[key] ?? EMPTY;
      return {
        byContext: {
          ...state.byContext,
          [key]: { ...ctx, messages: [...ctx.messages, message] },
        },
      };
    });
  },

  updateMessage: (key, messageId, updates) => {
    set((state) => {
      const ctx = state.byContext[key];
      if (!ctx) return state;
      return {
        byContext: {
          ...state.byContext,
          [key]: {
            ...ctx,
            messages: ctx.messages.map((m) => (m.id === messageId ? { ...m, ...updates } : m)),
          },
        },
      };
    });
  },

  appendDelta: (key, messageId, blockIndex, delta) => {
    set((state) => {
      const ctx = state.byContext[key];
      if (!ctx) return state;
      const messages = ctx.messages.map((m) => {
        if (m.id !== messageId) return m;
        const content: ContentBlock[] = [...m.content];
        const block = content[blockIndex];
        if (!block) {
          content[blockIndex] = { type: 'text', text: delta };
        } else if (block.type === 'text') {
          content[blockIndex] = { ...block, text: block.text + delta };
        } else if (block.type === 'markdown') {
          content[blockIndex] = { ...block, markdown: block.markdown + delta };
        }
        return { ...m, content };
      });
      return {
        byContext: { ...state.byContext, [key]: { ...ctx, messages } },
      };
    });
  },

  setStreaming: (key, isStreaming, messageId = null) => {
    set((state) => {
      const ctx = state.byContext[key] ?? EMPTY;
      return {
        byContext: {
          ...state.byContext,
          [key]: { ...ctx, isStreaming, streamingMessageId: messageId },
        },
      };
    });
  },

  setError: (key, message) => {
    set((state) => {
      const ctx = state.byContext[key] ?? EMPTY;
      return {
        byContext: {
          ...state.byContext,
          [key]: {
            ...ctx,
            error: message,
            isStreaming: false,
            streamingMessageId: null,
          },
        },
      };
    });
  },

  clearContext: (key) => {
    set((state) => {
      const next: Record<string, AgentPanelContextState> = {};
      for (const [k, v] of Object.entries(state.byContext)) {
        if (k !== key) next[k] = v;
      }
      return { byContext: next };
    });
  },
}));
